/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import BookingInterface2 from "./InstantBooking";

export default function TutorTimingCalender(props) {
  const [userTimeZone, setUserTimeZone] = useState("Asia/Dubai");
  const [tutorData, setTutorData] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [instant, setInstant] = useState(false);
  const [tempSelection, setTempSelection] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const { id } = useParams();

  // Fetch user timezone
  useEffect(() => {
    if (localStorage.getItem("TH_AUTH_TOKEN") === null) return;
    const fetchUserData = async () => {
      try {
        const res = await axios.get(
          "https://api.nadwa-uae.com/api/v2/users/profile/user_profile/",
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("TH_AUTH_TOKEN")}`,
            },
          }
        );
        setUserTimeZone(res.data.data.user.user_timezone);
      } catch (error) {
        console.log("Failed to fetch user data.");
      }
    };
    fetchUserData();
  }, []);

  // Fetch tutor data
  useEffect(() => {
    const fetchTutorData = async () => {
      try {
        const res = await axios.get(
          `https://api.nadwa-uae.com/api/v2/tutor/${props.tutorId || id}/`
        );
        setTutorData(res.data);
      } catch (error) {
        console.log("Failed to fetch tutor data.");
      }
    };
    fetchTutorData();
  }, [props.tutorId, id]);

  // Fetch booked slots for the tutor
  useEffect(() => {
    if (!tutorData?.user?.id) return;
    const fetchBookedSlots = async () => {
      try {
        const res = await axios.get(
          `https://api.nadwa-uae.com/api/v2/tutor/${tutorData.user.id}/bookings/`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("TH_AUTH_TOKEN")}`,
            },
          }
        );
        setBookedSlots(res.data);
      } catch (error) {
        console.log("Error fetching booked slots.");
      }
    };
    fetchBookedSlots();
  }, [tutorData?.user?.id]);

  // Generate available time slots
  const generateTimeSlots = useCallback(() => {
    if (!tutorData || !userTimeZone) return;

    const allSlots = [];
    const tutorTimeZone = tutorData.user_timezone;

    // Generate all 30-minute slots from availability in tutor's time zone
    tutorData.availability.forEach(({ start_time, end_time, day }) => {
      // Set the date to the specified day in the tutor's time zone
      const date = moment.tz(tutorTimeZone).add(weekOffset, "weeks").day(day);
      let start = moment.tz(
        `${date.format("YYYY-MM-DD")} ${start_time}`,
        tutorTimeZone
      );
      let end = moment.tz(
        `${date.format("YYYY-MM-DD")} ${end_time}`,
        tutorTimeZone
      );

      while (start.isBefore(end)) {
        const userTime = start.clone().tz(userTimeZone);
        allSlots.push({
          time: userTime.format("HH:mm"),
          day: userTime.isoWeekday(),
          rowStart: userTime.hour(),
          fullDateTime: userTime.format("YYYY-MM-DD HH:mm:ss"),
          isMorning: userTime.hour() < 12,
        });
        start.add(30, "minutes");
      }
    });

    // Filter out slots within tutor's time off periods
    const availableSlots = allSlots.filter((slot) => {
      const slotStart = moment.tz(slot.fullDateTime, userTimeZone);
      const isTimeOff = tutorData.time_off?.some((to) => {
        const toStart = moment.parseZone(to.start_datetime).tz(userTimeZone);
        const toEnd = moment.parseZone(to.end_datetime).tz(userTimeZone);
        return slotStart.isBetween(toStart, toEnd, undefined, "[)");
      });
      return !isTimeOff;
    });

    // Remove past time slots for the current day
    const now = moment.tz(userTimeZone);
    const availableSlotsNoPast = availableSlots.filter((slot) => {
      const slotMoment = moment.tz(slot.fullDateTime, userTimeZone);
      if (slotMoment.isSame(now, "day")) {
        return slotMoment.isAfter(now);
      }
      return true;
    });

    // Remove slots that overlap with booked slots
    const availableSlotsNoBooked = availableSlotsNoPast.filter((slot) => {
      if (!bookedSlots || bookedSlots.length === 0) return true;
      const slotMoment = moment.tz(slot.fullDateTime, userTimeZone);
      const isBooked = bookedSlots.some((booking) => {
        const bookingStart = moment.utc(booking.start_time).tz(userTimeZone);
        const bookingEnd = moment.utc(booking.end_time).tz(userTimeZone);
        return slotMoment.isBetween(bookingStart, bookingEnd, undefined, "[)");
      });
      return !isBooked;
    });

    // Create a set of available times per day for 1-hour block check
    const availableTimesByDay = {};
    availableSlotsNoBooked.forEach((slot) => {
      if (!availableTimesByDay[slot.day]) {
        availableTimesByDay[slot.day] = new Set();
      }
      availableTimesByDay[slot.day].add(slot.time);
    });

    // Filter for slots where the next 30-minute slot exists (i.e., 1-hour blocks)
    const filteredSlots = availableSlotsNoBooked.filter((slot) => {
      const nextTime = moment(slot.fullDateTime)
        .add(30, "minutes")
        .format("HH:mm");
      return availableTimesByDay[slot.day]?.has(nextTime);
    });

    setTimeSlots(filteredSlots);
  }, [tutorData, userTimeZone, weekOffset, bookedSlots]);

  useEffect(() => {
    generateTimeSlots();
  }, [generateTimeSlots]);

  // Define displayedDate as the start of the week (Monday)
  const displayedDate = moment().startOf("isoWeek").add(weekOffset, "weeks");
  const days = [...Array(7)].map((_, index) => {
    const date = displayedDate.clone().add(index, "days");
    return {
      date: date.date(),
      day: date.format("ddd"),
      dayNumber: date.isoWeekday(),
      fullDate: date.format("YYYY-MM-DD"),
    };
  });

  const getSlots = (dayNumber, rowIndex) => {
    return timeSlots.filter(
      (slot) => slot.day === dayNumber && slot.rowStart === rowIndex
    );
  };

  const goToPreviousWeek = () => setWeekOffset((prev) => Math.max(prev - 1, 0));
  const goToNextWeek = () => setWeekOffset((prev) => prev + 1);

  const handleSlotClick = (slot) => {
    const token = localStorage.getItem("TH_AUTH_TOKEN");
    if (!token) return (window.location.href = "/login");

    if (!tutorData.has_stripe_account) {
      return toast.error(
        "Tutor has not set up bank account for payments. You can't book this tutor"
      );
    }

    // Use the slot's fullDateTime directly without adding weekOffset again
    const clickedDate = moment(slot.fullDateTime).format("YYYY-MM-DD");
    const now = moment().startOf("day");
    let selectedSlotDate = moment(clickedDate).startOf("day");

    if (selectedSlotDate.isBefore(now)) {
      selectedSlotDate = selectedSlotDate.add(7, "days");
    }

    const slotTime = slot.time;
    const nextTime = moment(slot.fullDateTime)
      .add(30, "minutes")
      .format("HH:mm");

    setTempSelection({ date: selectedSlotDate.format("YYYY-MM-DD"), times: [slotTime, nextTime] });
    setInstant(true);
  };

  const activeHours = [...new Set(timeSlots.map((slot) => slot.rowStart))].sort(
    (a, b) => a - b
  );
  const weekStart = displayedDate.clone();
  const weekEnd = displayedDate.clone().add(6, "days");

  // Define months and years for dropdowns
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const currentYear = moment().year();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  return (
    <div className="md:px-10 md:py-10 ">
      <ToastContainer />
      <div className="flex rounded-md justify-between gap-4 md:flex-row flex-col items-center mb-4 bg-[#F0F3F599] py-3 md:px-4">
        <div className="text-xl font-bold">Availability</div>
        <div className="flex flex-col items-center">
          <div className="text-[#FF7291] mb-2 text-sm md:text-base">
            {userTimeZone + " " + "Time"}
          </div>
          {activeHours.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                value={displayedDate.month()}
                onChange={(e) => {
                  const newMonth = parseInt(e.target.value);
                  const newDisplayedDate = moment(displayedDate).month(newMonth).startOf("month").startOf("isoWeek");
                  const currentStart = moment().startOf("isoWeek");
                  const newWeekOffset = newDisplayedDate.diff(currentStart, "weeks");
                  setWeekOffset(newWeekOffset);
                }}
                className="p-1 border rounded"
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
              <select
                value={displayedDate.year()}
                onChange={(e) => {
                  const newYear = parseInt(e.target.value);
                  const newDisplayedDate = moment(displayedDate).year(newYear).startOf("isoWeek");
                  const currentStart = moment().startOf("isoWeek");
                  const newWeekOffset = newDisplayedDate.diff(currentStart, "weeks");
                  setWeekOffset(newWeekOffset);
                }}
                className="p-1 border rounded"
              >
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <button onClick={goToPreviousWeek}>
                <ChevronLeft />
              </button>
              <div className="text-lg md:text-2xl font-bold text-center">
                {weekStart.format("MMMM DD")} - {weekEnd.format("DD MMMM YYYY")}
              </div>
              <button onClick={goToNextWeek}>
                <ChevronRight />
              </button>
              <button onClick={() => setWeekOffset(0)} className="ml-2 text-blue-600 hover:underline">
                Today
              </button>
            </div>
          )}
        </div>
        <div>
          <button
            onClick={() => {
              const token = localStorage.getItem("TH_AUTH_TOKEN");
              if (!token) return (window.location.href = "/login");
              else setInstant(true);
            }}
            className="rounded-full px-3 py-2 text-sm md:text-base text-white bg-[#ED1D24]"
          >
            Open Full Calendar
          </button>
        </div>
      </div>
      {activeHours.length > 0 ? (
        <div className="max-h-[400px] md:max-h-[500px] px-4 md:w-[80%] w-[95%] lg:w-1/2 mx-auto border-[#008847] border-2 rounded-3xl overflow-y-scroll relative">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-white border-b-2 border-[#4A94594D] h-[80px] md:h-[100px]">
              <tr>
                {days.map((day) => (
                  <th key={day.dayNumber} className="text-center">
                    <div className="font-bold text-black md:text-xl">{day.date}</div>
                    <div className="md:text-lg text-[#4A9459]">{day.day}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeHours.map((rowIndex) => (
                <tr key={rowIndex}>
                  {days.map((day) => {
                    const slots = getSlots(day.dayNumber, rowIndex);
                    return (
                      <td key={day.dayNumber}>
                        <div className="flex flex-col gap-1 h-full items-center justify-center">
                          {slots.map((slot, index) => (
                            <div
                              onClick={() => handleSlotClick(slot)}
                              key={index}
                              className="cursor-pointer text-center rounded-md text-[16px] font-bold p-1 w-full transition-opacity duration-200 hover:opacity-90 text-black"
                            >
                              <span>{slot.time}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-gray-500 py-10">
          No available slots for this week.
        </div>
      )}
      {instant && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg relative w-[90%]">
            <BookingInterface2
              showModal={instant}
              setInstant={(value) => {
                setInstant(value);
                if (!value) setTempSelection(null);
              }}
              tutorData={tutorData}
              initialSelectedTimesByDate={
                tempSelection
                  ? { [tempSelection.date]: tempSelection.times }
                  : {}
              }
              initialDate={tempSelection?.date}
            />
          </div>
        </div>
      )}
    </div>
  );
}